import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { CheckCircle2, PackageOpen, Plus } from 'lucide-react';
import { listLoans, getLoanQueue } from '../../api/loans';
import { useAuth } from '../../auth/useAuth';
import { hasRole, primaryOrgId } from '../../auth/permissions';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card, CardHeader } from '../../components/ui/Card';
import { Table, Thead, Th, Tr, Td } from '../../components/ui/Table';
import { StatusBadge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { EmptyState } from '../../components/ui/EmptyState';
import { Button } from '../../components/ui/Button';
import { RequestLoanModal } from './RequestLoanModal';
import { ApproveLoanModal } from './ApproveLoanModal';
import { RejectLoanModal } from './RejectLoanModal';
import type { AssetLoan } from '../../types/api';

function itemName(loan: AssetLoan) {
  return loan.asset.orderLine?.customerCatalogItem?.name ?? 'Asset';
}

function requesterName(loan: AssetLoan) {
  return [loan.requestedBy.firstName, loan.requestedBy.lastName].filter(Boolean).join(' ') || loan.requestedBy.email;
}

function LoanRow({ loan }: { loan: AssetLoan }) {
  return (
    <Tr>
      <Td className="font-medium text-slate-900">
        <Link to={`/loans/${loan.id}`} className="hover:text-brand-700">
          {itemName(loan)}
        </Link>
      </Td>
      <Td>{loan.site.name}</Td>
      <Td>{requesterName(loan)}</Td>
      <Td>{new Date(loan.dueDate).toLocaleDateString()}</Td>
      <Td>
        <StatusBadge status={loan.status} kind="loan" />
      </Td>
    </Tr>
  );
}

export function LoansPage() {
  const { user } = useAuth();
  const isMspAdmin = user?.isMspAdmin ?? false;
  const canApprove = isMspAdmin || hasRole(user, 'SIGNING_AUTHORITY');
  const canRequest = !isMspAdmin && !!primaryOrgId(user);

  const [requestOpen, setRequestOpen] = useState(false);
  const [approveTarget, setApproveTarget] = useState<AssetLoan | null>(null);
  const [rejectTarget, setRejectTarget] = useState<AssetLoan | null>(null);

  const loansQuery = useQuery({ queryKey: ['loans'], queryFn: () => listLoans() });
  const queueQuery = useQuery({ queryKey: ['loans-queue'], queryFn: getLoanQueue, enabled: canApprove });

  return (
    <div>
      <PageHeader
        title="Loans"
        description="Borrow spare equipment, or manage requests and check-outs."
        actions={
          canRequest && (
            <Button onClick={() => setRequestOpen(true)}>
              <Plus className="h-3.5 w-3.5" /> Request a loan
            </Button>
          )
        }
      />

      <div className="flex flex-col gap-6">
        {canApprove && (
          <Card>
            <CardHeader>
              <span className="text-sm font-medium text-slate-600">Pending your approval</span>
            </CardHeader>
            {queueQuery.isLoading ? (
              <div className="py-10 text-center">
                <Spinner />
              </div>
            ) : !queueQuery.data || queueQuery.data.length === 0 ? (
              <EmptyState icon={CheckCircle2} title="Nothing waiting" description="You're all caught up." />
            ) : (
              <Table>
                <Thead>
                  <tr>
                    <Th>Item</Th>
                    <Th>Requested by</Th>
                    <Th>Site</Th>
                    <Th>Due back</Th>
                    <Th />
                  </tr>
                </Thead>
                <tbody>
                  {queueQuery.data.map((loan) => (
                    <Tr key={loan.id}>
                      <Td className="font-medium text-slate-900">
                        <Link to={`/loans/${loan.id}`} className="hover:text-brand-700">
                          {itemName(loan)}
                        </Link>
                      </Td>
                      <Td>{requesterName(loan)}</Td>
                      <Td>{loan.site.name}</Td>
                      <Td>{new Date(loan.dueDate).toLocaleDateString()}</Td>
                      <Td className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button size="sm" variant="danger" onClick={() => setRejectTarget(loan)}>
                            Reject
                          </Button>
                          <Button size="sm" onClick={() => setApproveTarget(loan)}>
                            Approve
                          </Button>
                        </div>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>
        )}

        <Card>
          <CardHeader>
            <span className="text-sm font-medium text-slate-600">All loans</span>
          </CardHeader>
          {loansQuery.isLoading ? (
            <div className="py-14 text-center">
              <Spinner />
            </div>
          ) : !loansQuery.data || loansQuery.data.length === 0 ? (
            <EmptyState icon={PackageOpen} title="No loans yet" description="Loan requests will show up here." />
          ) : (
            <Table>
              <Thead>
                <tr>
                  <Th>Item</Th>
                  <Th>Site</Th>
                  <Th>Requested by</Th>
                  <Th>Due back</Th>
                  <Th>Status</Th>
                </tr>
              </Thead>
              <tbody>
                {loansQuery.data.map((loan) => (
                  <LoanRow key={loan.id} loan={loan} />
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      </div>

      {requestOpen && <RequestLoanModal open={requestOpen} onClose={() => setRequestOpen(false)} />}
      {approveTarget && (
        <ApproveLoanModal loan={approveTarget} open={!!approveTarget} onClose={() => setApproveTarget(null)} />
      )}
      {rejectTarget && (
        <RejectLoanModal loan={rejectTarget} open={!!rejectTarget} onClose={() => setRejectTarget(null)} />
      )}
    </div>
  );
}
